<template>
  <div class="flex-child">
    <div class="vue-chart">
      <el-skeleton :loading="isLoadingUpdateDataDashboardInjected" animated>
        <template #template>
          <el-skeleton-item style="height: 315px" />
        </template>
        <template #default>
          <apexchart width="100%" height="300" type="pie" :options="chartOptions" :series="series" />
        </template>
      </el-skeleton>
    </div>
    <div class="flex-text">
      <p
        v-html="$t('dashboardAvailableForPurchaseAmount', { label: formatters.label.call(currentTokenInfo?.name) })"
      ></p>
      <p
        v-if="currentToken === Tokens.pearl"
        v-html="$t('dashboardPurchased', { label: formatters.label.call(currentTokenInfo?.name) })"
      ></p>
      <template v-if="currentToken === Tokens.pearl">
        <p v-html="$t('dashboardScatteredOnSeabed', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
        <p v-html="$t('dashboardRetrievedByDivers', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
      </template>
      <template v-else>
        <p v-html="$t('dashboardHiddenOnIsland', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
        <p v-html="$t('dashboardRetrievedOnIsland', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ChartDataFormatterProps } from '~/types/common';
import { Tokens } from '~/utils/constants';
import type { ITokenModel } from '~/types/apiService';
import { colors } from '~/utils/constants/charts';

const { t } = useI18n();
const store = useMainStore();

const props = defineProps<ChartDataFormatterProps>();

const { data: currentTokenInfo } = useNuxtData<ITokenModel>('dashboardCurrentTokenInfo');

const isLoadingUpdateDataDashboardInjected = inject<Ref<boolean>>('isLoadingUpdateDataDashboard');
const saleAmountInjected = inject<Ref<number>>('saleAmountValue', ref(0));
const soldInjected = inject<Ref<number>>('soldAmountValue', ref(0));
const maxTotalSupplyInjected = inject<Ref<number>>('maxTotalSupply', ref(0));
const totalClaimedInjected = inject<Ref<number>>('totalClaimed', ref(0));

const currentToken = computed(() => store.currentToken);

const labels = computed(() =>
  currentToken.value === Tokens.pearl
    ? [
        t('dashboardAvailableForPurchaseLabel'),
        t('dashboardPurchasedLabel'),
        t('dashboardScatteredOnSeabedLabel'),
        t('dashboardRetrievedByPearlDiversLabel')
      ]
    : [t('dashboardAvailableForPurchaseLabel'), t('dashboardHiddenOnIslandLabel'), t('dashboardRetrievedOnIslandLabel')]
);

const series = computed(() => {
  const notMiningTokenDataSet = [
    saleAmountInjected.value,
    soldInjected.value,
    maxTotalSupplyInjected.value,
    totalClaimedInjected.value
  ];

  const miningTokenDataSet = [saleAmountInjected.value, maxTotalSupplyInjected.value, totalClaimedInjected.value];

  return store.currentToken === Tokens.pearl ? notMiningTokenDataSet : miningTokenDataSet;
});

const chartOptions = computed(() => ({
  theme: {
    mode: 'dark'
  },
  chart: {
    class: 'vuechart-example',
    background: '#00000000'
  },
  labels: labels.value,
  title: {
    text: t('coreAvailability'),
    align: 'center',
    style: {
      fontSize: '24px'
    }
  },
  legend: {
    fontSize: '12px',
    offsetY: 30
  },
  stroke: {
    width: 0
  },
  ...colors,
  tooltip: {
    y: {
      formatter: function (val: number) {
        return props.formatters.tooltip.call(val);
      }
    },
    fillSeriesColor: true
  },
  responsive: [
    {
      breakpoint: 600,
      options: {
        legend: {
          position: 'bottom',
          offsetY: 0
        },
        chart: {
          height: 500
        }
      }
    }
  ]
}));
</script>

<style lang="scss" scoped>
.vue-chart {
  padding-bottom: 12px;
  min-height: 340px;
}
</style>
